/**
 * Cache Store (Zustand)
 *
 * Global state management for cache operations, including
 * refresh progress tracking and cache status monitoring.
 *
 * SECURITY NOTE (SSE Authentication):
 * - EventSource API cannot send custom headers (Authorization: Bearer)
 * - JWT token passed via query parameter for SSE endpoints only
 * - Safe in production because:
 *   1. HTTPS encrypts entire URL (including query params)
 *   2. Tokens are short-lived (30 min default expiration)
 *   3. Backend validates token on every request
 *   4. Tokens never logged to console (use 'with authenticated token' message)
 * - Alternative approaches (fetch + ReadableStream) add complexity without security benefit
 */

import { create } from 'zustand';
import { getCacheStatus } from '../services/cacheService';
import type { CacheStatusResponse } from '../types/api';
import { logger } from '../utils/logger';
import { useEPGStore } from './epgStore';
import { useEventStore } from '../../features/epg/stores/eventStore';

/**
 * Get the SSE endpoint URL.
 *
 * NOTE: Must use same routing as apiClient for consistent cookie handling:
 * - Production (eroland.me): Use relative URL → Cloudflare Functions proxy → Railway
 *   This keeps cookies first-party (cookies set for eroland.me work with eroland.me requests)
 * - Development: Use relative URL → Vite proxy → Railway
 *
 * CRITICAL: Do NOT use direct Railway URL in production - cookies won't be sent
 * because they're set as first-party cookies via the Cloudflare proxy.
 */
const getSSEBaseUrl = (): string => {
  // Check localStorage override first (for testing direct Railway connection)
  if (typeof window !== 'undefined') {
    const override = localStorage.getItem('api_base_url_override');
    if (override) {
      return override;
    }
  }

  // PRODUCTION FIX: Use relative path so requests go through Cloudflare Functions proxy
  // This ensures cookies (set as first-party via proxy) are sent with requests
  if (typeof window !== 'undefined' && window.location.host === 'eroland.me') {
    logger.log('[CacheStore] Production: Using relative URL (Cloudflare proxy for first-party cookies)');
    return '';
  }

  // Development: Use relative URL (Vite proxy handles routing to Railway)
  logger.log('[CacheStore] Development: Using relative URL for SSE (Vite proxy)');
  return '';
};

/**
 * Get a short-lived SSE token from the backend.
 *
 * Since auth tokens are now stored in HttpOnly cookies (not accessible to JS),
 * we need to call the /auth/ws-token endpoint which:
 * 1. Reads the HttpOnly access_token cookie
 * 2. Validates it
 * 3. Returns a short-lived (60s) token we can use for SSE
 *
 * @returns JWT token for SSE connection or null if not authenticated
 */
const getSSEToken = async (): Promise<string | null> => {
  try {
    // Use same base URL logic as getSSEBaseUrl for consistent cookie handling
    // In production, this goes through Cloudflare proxy to keep cookies first-party
    const baseUrl = getSSEBaseUrl();

    const response = await fetch(`${baseUrl}/api/auth/ws-token`, {
      method: 'GET',
      credentials: 'include', // Include HttpOnly cookies
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      logger.error('[CacheStore] Failed to get SSE token:', response.status, response.statusText);
      return null;
    }

    const data = await response.json();
    return data.ws_token || null;
  } catch (error) {
    logger.error('[CacheStore] Error getting SSE token:', error);
    return null;
  }
};

/**
 * Debounce time for cache sync protection (500ms)
 */
const SYNC_DEBOUNCE_MS = 500;

/**
 * Cache store state and actions
 */
interface CacheStore {
  // Status
  isRefreshing: boolean;
  progress: number;
  message: string | null;
  lastRefresh: string | null;
  organizationCount: number;
  contactCount: number;
  error: string | null;

  // Confirmation dialog state
  syncConfirmOpen: boolean;
  lastSyncAttempt: number | null;

  // Actions
  refreshCache: (options?: { confirmed?: boolean }) => Promise<void>;
  getCacheStatus: () => Promise<void>;
  updateProgress: (progress: number, message: string) => void;
  clearError: () => void;
  reset: () => void;
  setSyncConfirmOpen: (open: boolean) => void;
}

/**
 * Initial state
 */
const initialState = {
  isRefreshing: false,
  progress: 0,
  message: null,
  lastRefresh: null,
  organizationCount: 0,
  contactCount: 0,
  error: null,
  syncConfirmOpen: false,
  lastSyncAttempt: null,
};

/**
 * Create cache store
 */
export const useCacheStore = create<CacheStore>((set, get) => ({
  ...initialState,

  /**
   * Trigger cache refresh from Pipedrive
   *
   * Uses SSE (EventSource) to stream progress updates in real-time.
   * Requires explicit confirmation to prevent accidental sync operations.
   * Includes debounce protection against rapid repeated attempts.
   *
   * @param options - Optional configuration
   * @param options.confirmed - Whether user has confirmed the sync operation
   *
   * @example
   * ```tsx
   * const { refreshCache } = useCacheStore();
   *
   * // First call - opens confirmation dialog
   * await refreshCache();
   *
   * // Second call with confirmation - actually executes
   * await refreshCache({ confirmed: true });
   * ```
   */
  refreshCache: async (options?: { confirmed?: boolean }) => {
    const state = get();

    // Require explicit confirmation
    if (!options?.confirmed) {
      set({ syncConfirmOpen: true });
      return;
    }

    // Debounce protection
    const now = Date.now();
    if (state.lastSyncAttempt && now - state.lastSyncAttempt < SYNC_DEBOUNCE_MS) {
      logger.warn('[CacheStore] Sync debounced - too many rapid attempts');
      return;
    }

    set({
      isRefreshing: true,
      progress: 0,
      error: null,
      message: 'Getting authentication token...',
      syncConfirmOpen: false,
      lastSyncAttempt: now,
    });

    // Get short-lived SSE token from backend
    // This endpoint reads the HttpOnly cookie and returns a temporary token
    let token: string | null = null;

    // Check if in dev mode with skip auth - use dev API key instead
    const isDevSkipAuth = import.meta.env.DEV && import.meta.env.VITE_DEV_SKIP_AUTH === 'true';
    const devApiKey = import.meta.env.VITE_DEV_API_KEY || 'dev-key-12345';

    if (isDevSkipAuth) {
      token = `dev:${devApiKey}`;
      logger.log('[CacheStore] Using dev API key for SSE');
    } else {
      // Get SSE token from backend (which reads HttpOnly cookie)
      set({ message: 'Authenticating...' });
      token = await getSSEToken();
    }

    if (!token) {
      const errorMsg = 'Authentication required. Please log in to sync cache.';
      logger.error('[CacheStore] No authentication token found');
      set({
        error: errorMsg,
        isRefreshing: false,
        progress: 0,
        message: null,
      });
      return Promise.reject(new Error(errorMsg));
    }

    set({ message: 'Starting refresh...' });

    return new Promise<void>((resolve, reject) => {
      // Connect to SSE stream
      // In production, use full Railway URL. In development, use Vite proxy.
      const baseUrl = getSSEBaseUrl();
      // SECURITY: Token passed via query param (EventSource limitation - cannot send headers)
      // Safe because: HTTPS encrypts URL, tokens are short-lived (30min expiry)
      const url = `${baseUrl}/api/cache/refresh?token=${encodeURIComponent(token)}`;
      logger.log('[CacheStore] Full SSE URL:', url.replace(/token=[^&]+/, 'token=***'));
      logger.log('[CacheStore] Environment:', {
        PROD: import.meta.env.PROD,
        VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
      });

      const eventSource = new EventSource(url);
      let hasReceivedData = false;

      eventSource.onopen = () => {
      };

      eventSource.onmessage = (event) => {
        hasReceivedData = true;
        try {
          const data = JSON.parse(event.data);

          // Check for completion
          if (data.status === 'completed') {
            eventSource.close();
            set({
              isRefreshing: false,
              progress: 100,
              message: 'Refresh complete',
            });

            // Get updated cache status
            get().getCacheStatus();

            // Invalidate EPG organization cache and trigger refresh
            // This ensures new Pipedrive field values (like "Rockets" in January) appear after sync
            logger.log('[CacheStore] Invalidating EPG organization cache after sync');
            useEPGStore.setState({ lastOrganizationsFetchTime: null });
            useEPGStore.getState().loadOrganizations().catch((err) => {
              logger.warn('[CacheStore] EPG organization refresh failed (non-critical):', err);
            });

            // Sync field definitions from Pipedrive, THEN refresh event options
            // Field definitions contain the dropdown options (like "Rockets") - must sync backend cache first
            logger.log('[CacheStore] Syncing field definitions from Pipedrive after cache sync');
            const eventStore = useEventStore.getState();
            eventStore.syncFieldDefinitions()
              .then(() => {
                // Now refresh EPG event options (dropdown values like "Rockets") for all categories
                // These read from the just-updated backend field definitions cache
                logger.log('[CacheStore] Refreshing EPG event options for all categories');
                return Promise.all([
                  eventStore.loadEventOptions('Events'),
                  eventStore.loadEventOptions('Calls & Emails'),
                  eventStore.loadEventOptions('Drops'),
                ]);
              })
              .catch((err) => {
                logger.warn('[CacheStore] EPG field definitions/event options refresh failed (non-critical):', err);
              });

            resolve();
          } else if (data.status === 'error') {
            logger.error('[CacheStore] Refresh error:', data.message);
            eventSource.close();
            const errorMsg = data.message || 'Cache refresh failed';
            set({
              error: errorMsg,
              isRefreshing: false,
              progress: 0,
              message: null,
            });
            reject(new Error(errorMsg));
          } else if (data.percentage !== undefined) {
            // Progress update
            set({
              progress: data.percentage,
              message: data.message || `${data.percentage}%`,
            });
          }
        } catch (error) {
          logger.error('[CacheStore] Failed to parse SSE event:', error);
        }
      };

      eventSource.onerror = (error) => {
        // Enhanced traceback logging for SSE errors
        console.error('[CacheStore] SSE connection error - TRACEBACK:', {
          timestamp: new Date().toISOString(),
          readyState: eventSource.readyState,
          readyStateLabel: ['CONNECTING', 'OPEN', 'CLOSED'][eventSource.readyState] || 'UNKNOWN',
          url: url.replace(/token=[^&]+/, 'token=***'),
          hasReceivedData,
        });
        console.error('[CacheStore] SSE error object:', error);
        if (error instanceof Error) {
          console.error('[CacheStore] SSE error message:', error.message);
          console.error('[CacheStore] SSE error stack:', error.stack);
        }
        // Try to serialize the error for full inspection
        try {
          console.error('[CacheStore] SSE error JSON:', JSON.stringify(error, Object.getOwnPropertyNames(error as object), 2));
        } catch {
          console.error('[CacheStore] SSE error (non-serializable):', error);
        }
        logger.error('[CacheStore] SSE connection error:', error);
        logger.error('[CacheStore] EventSource readyState:', eventSource.readyState);
        logger.error('[CacheStore] EventSource URL:', url.replace(/token=[^&]+/, 'token=***'));
        eventSource.close();

        // EventSource readyState values:
        // 0 = CONNECTING, 1 = OPEN, 2 = CLOSED
        let errorMessage = 'Connection lost during cache refresh';

        // Provide more specific error messages based on connection state
        if (!hasReceivedData) {
          if (eventSource.readyState === EventSource.CLOSED) {
            const backendUrl = getSSEBaseUrl() || 'the backend server';
            errorMessage = `Failed to connect to server. The backend might be down or unreachable. Please check that the API server is running at ${backendUrl}`;
          } else {
            errorMessage = 'Authentication failed. Please log in again or check your token expiration.';
          }
        }

        set({
          error: errorMessage,
          isRefreshing: false,
          progress: 0,
          message: null,
        });
        reject(new Error(errorMessage));
      };

      // Timeout after 5 minutes
      const timeoutId = setTimeout(() => {
        if (eventSource.readyState !== EventSource.CLOSED) {
          logger.warn('[CacheStore] Cache refresh timed out after 5 minutes');
          eventSource.close();
          set({
            error: 'Cache refresh timed out after 5 minutes. The operation may still be running in the background.',
            isRefreshing: false,
            progress: 0,
            message: null,
          });
          reject(new Error('Timeout'));
        }
      }, 5 * 60 * 1000);

      // Clean up timeout on success/error
      const originalResolve = resolve;
      const originalReject = reject;
      resolve = (...args) => {
        clearTimeout(timeoutId);
        originalResolve(...args);
      };
      reject = (...args) => {
        clearTimeout(timeoutId);
        originalReject(...args);
      };
    });
  },

  /**
   * Get current cache status
   *
   * Fetches metadata about cache (last refresh time, record counts).
   *
   * @example
   * ```tsx
   * const { getCacheStatus } = useCacheStore();
   *
   * await getCacheStatus();
   * ```
   */
  getCacheStatus: async () => {
    try {
      const status: CacheStatusResponse = await getCacheStatus();

      set({
        lastRefresh: status.last_refresh,
        organizationCount: status.organizations_count,
        contactCount: status.contacts_count,
        isRefreshing: status.is_refreshing,
      });
    } catch (error) {
      logger.error('[CacheStore] Failed to fetch cache status:', error);
      // Don't set error state for status checks (non-critical)
    }
  },

  /**
   * Update progress from SSE events
   *
   * Called by useCacheSync hook when SSE events arrive.
   *
   * @param progress - Progress percentage (0-100)
   * @param message - Progress message
   *
   * @example
   * ```tsx
   * const { updateProgress } = useCacheStore();
   *
   * updateProgress(50, 'Fetching organizations...');
   * ```
   */
  updateProgress: (progress: number, message: string) => {
    set({ progress, message });
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(initialState);
  },

  /**
   * Set sync confirmation dialog open state
   *
   * @param open - Whether the confirmation dialog should be open
   */
  setSyncConfirmOpen: (open: boolean) => {
    set({ syncConfirmOpen: open });
  },
}));
